import { getWeekNumber } from './helpers';
import { getWeekDateRange } from './timeUtils';

type WeekSelection = {
    week: number;
    year: number;
} | null | undefined;

type TimelineRange = {
    startWeek?: number;
    endWeek?: number;
} | null | undefined;

type PeriodLike = {
    id?: string | number;
    name?: string;
    start_week?: number;
    end_week?: number;
    year?: number;
} | null | undefined;

function getIsoWeekYear(date: Date): number {
    const thursday = new Date(date);
    const day = thursday.getDay() || 7;
    thursday.setDate(thursday.getDate() + 4 - day);
    return thursday.getFullYear();
}

export type ScheduleTimelineContext = {
    weekNumber: number;
    year: number;
    date: Date;
    source: 'selectedWeek' | 'timelineRange' | 'selectedPeriod' | 'today';
};

/**
 * Derive a consistent scheduling context (week/year/date) based on the current
 * calendar filters or fall back to the current date.
 */
export function deriveScheduleTimelineContext(options: {
    selectedWeek?: WeekSelection;
    timelineViewRange?: TimelineRange & { startWeek?: number; endWeek?: number };
    selectedYearForView?: number | null;
    selectedPeriod?: PeriodLike;
    fallbackDate?: Date;
}): ScheduleTimelineContext {
    const {
        selectedWeek,
        timelineViewRange,
        selectedYearForView,
        selectedPeriod,
        fallbackDate
    } = options;

    if (selectedWeek && typeof selectedWeek.week === 'number' && typeof selectedWeek.year === 'number') {
        const range = getWeekDateRange(selectedWeek.year, selectedWeek.week);
        return {
            weekNumber: selectedWeek.week,
            year: selectedWeek.year,
            date: range?.startDate ?? new Date(selectedWeek.year, 0, 1),
            source: 'selectedWeek'
        };
    }

    const hasSingleWeekRange = Boolean(
        timelineViewRange &&
        typeof timelineViewRange.startWeek === 'number' &&
        timelineViewRange.startWeek === timelineViewRange.endWeek
    );
    if (hasSingleWeekRange) {
        const year = selectedYearForView ?? new Date().getFullYear();
        const weekNumber = timelineViewRange!.startWeek as number;
        const range = getWeekDateRange(year, weekNumber);
        return {
            weekNumber,
            year,
            date: range?.startDate ?? new Date(year, 0, 1),
            source: 'timelineRange'
        };
    }

    if (selectedPeriod && typeof selectedPeriod.start_week === 'number') {
        const year = selectedPeriod.year ?? selectedYearForView ?? new Date().getFullYear();
        const weekNumber = selectedPeriod.start_week;
        const range = getWeekDateRange(year, weekNumber);
        return {
            weekNumber,
            year,
            date: range?.startDate ?? new Date(year, 0, 1),
            source: 'selectedPeriod'
        };
    }

    const fallback = fallbackDate ?? new Date();
    return {
        weekNumber: getWeekNumber(fallback),
        year: getIsoWeekYear(fallback),
        date: fallback,
        source: 'today'
    };
}

